import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Avatar,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import type { ChipProps } from '@mui/material'
import {
  Download,
  Upload,
  OpenInNew,
  Info,
  Delete,
  Explore,
  Public,
  Lock,
  FilterList,
  Refresh,
  Close,
  PlayArrow,
  CheckCircle,
  Cancel,
  PauseCircle,
  Person,
  ArrowBack,
  LocationOn,
  Timer,
  Lightbulb,
  Group,
  Warning,
  FactCheck,
} from '@mui/icons-material'
import { useSearchParams } from 'react-router-dom'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import type { RowAction } from '@components/common/RowActions'
import { SearchField } from '@components/common/SearchField'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { adminApi } from '@api/endpoints/admin'
import {
  AdminAdventureDto,
  AdminAdventuresListParams,
  AdminAdventureStats,
  AdminAdventureDeleteResult,
  AdminSessionDto,
  AdminSessionDetailDto,
  AdminSessionsListParams,
  SessionStatus,
  SessionLocationLogDto,
  ADVENTURE_STATUS_MAP,
  MODERATION_STATUS_MAP,
} from '@api/types/admin'
import { CategoryListTable } from '../components/categories'
import { AdventureImportDialog } from '../components/adventures/AdventureImportDialog'
import { useAdventureCategories } from '../hooks/useAdventureCategories'
import { getAdventureValidationCounts } from '@api/services/tickets.api'
import { AdventureValidationPanel } from '@features/tickets/components/AdventureValidationPanel'
import { AdventureStoryAccessPanel } from '@features/stories/components/AdventureStoryAccessPanel'

type SortField =
  | 'Relevance'
  | 'Title'
  | 'CreatedDate'
  | 'UpdatedDate'
  | 'Status'
  | 'ParticipantCount'
  | 'CreatorUsername'

// Status: 0=Draft, 1=Testing, 2=Published, 3=Archived
const getStatusColor = (status: number): ChipProps['color'] => {
  switch (status) {
    case 2: // Published
      return 'success'
    case 0: // Draft
      return 'default'
    case 1: // Testing
      return 'info'
    case 3: // Archived
      return 'warning'
    default:
      return 'default'
  }
}

// ModerationStatus: 0=Pending, 1=Approved, 2=Rejected, 3=Flagged
const getModerationStatusColor = (status: number): ChipProps['color'] => {
  switch (status) {
    case 1: // Approved
      return 'success'
    case 0: // Pending
      return 'warning'
    case 2: // Rejected
      return 'error'
    case 3: // Flagged
      return 'error'
    default:
      return 'default'
  }
}

const getSessionStatusColor = (status: SessionStatus): ChipProps['color'] => {
  switch (status) {
    case 'InProgress':
      return 'info'
    case 'Completed':
      return 'success'
    case 'Failed':
      return 'error'
    case 'Abandoned':
      return 'warning'
    default:
      return 'default'
  }
}

const getSessionStatusIcon = (status: SessionStatus) => {
  switch (status) {
    case 'InProgress':
      return <PlayArrow fontSize="small" />
    case 'Completed':
      return <CheckCircle fontSize="small" />
    case 'Failed':
      return <Cancel fontSize="small" />
    case 'Abandoned':
      return <PauseCircle fontSize="small" />
    default:
      return null
  }
}

/** Dates come off the API as strings and are sometimes absent; N/A beats "Invalid Date". */
const formatListDate = (dateStr?: string | null) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
}

const SESSIONS_DRAWER_WIDTH = 600

/**
 * The portal only moderates; it has no adventure page or editor of its own. "View" opens the
 * adventure where players see it (Website route /adventures/:id).
 */
const PLAYER_SITE_URL = 'https://play.hadv.quest'

/**
 * Filters live in the URL (`?search=`, `?status=`, `?moderation=`) so the dashboard and the stat
 * cards can deep-link to a filtered list, and Back restores it. Status and moderation are the
 * API's numeric enums (0-3); anything else is ignored.
 */
const parseEnumParam = (raw: string | null): number | '' => {
  if (raw === null || raw === '') return ''
  const n = Number(raw)
  return Number.isInteger(n) && n >= 0 && n <= 3 ? n : ''
}

/** Keyed by string so `?tab=` keeps pointing at the same tab whatever the order becomes. */
const TABS: ResponsiveTabItem[] = [
  { key: 'adventures', label: 'Adventure Management' },
  { key: 'categories', label: 'Category Management' },
]

export default function AdminAdventuresPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const mobileDialogProps = useMobileDialogProps()
  const [activeTab, setActiveTab] = useState('adventures')
  const [adventures, setAdventures] = useState<AdminAdventureDto[]>([])
  const [stats, setStats] = useState<AdminAdventureStats | null>(null)
  const [loading, setLoading] = useState(true)
  // `error` is for actions (export, delete); `listError` is the list fetch, shown by DataTable
  // with a Retry so a failed load never reads as "No adventures found".
  const [error, setError] = useState<string | null>(null)
  const [listError, setListError] = useState<string | null>(null)
  const [exporting, setExporting] = useState<string | null>(null)

  // Fetch categories for dynamic category name lookup
  const { data: categories } = useAdventureCategories(true)

  // Pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('CreatedDate')
  const [sortDescending, setSortDescending] = useState(true)
  // Whether the admin has clicked a column header. Until they do, the list chooses its own
  // order; once they have, their choice is left alone.
  const [sortPinned, setSortPinned] = useState(false)

  // Filters: read from the URL, written back with `replace` so typing does not stack history.
  const search = searchParams.get('search') ?? ''
  const statusFilter = parseEnumParam(searchParams.get('status'))
  const moderationFilter = parseEnumParam(searchParams.get('moderation'))
  const [showFilters, setShowFilters] = useState(statusFilter !== '' || moderationFilter !== '')

  const setFilters = useCallback(
    (changes: Partial<Record<'search' | 'status' | 'moderation', string | number | ''>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(changes)) {
            if (value === '' || value === undefined) next.delete(key)
            else next.set(key, String(value))
          }
          return next
        },
        { replace: true }
      )
      setPage(0)
    },
    [setSearchParams]
  )

  // Sessions drawer
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false)
  const [sessionsAdventure, setSessionsAdventure] = useState<AdminAdventureDto | null>(null)
  const [sessions, setSessions] = useState<AdminSessionDto[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [sessionsPage, setSessionsPage] = useState(0)
  const [sessionsPageSize, setSessionsPageSize] = useState(25)
  const [sessionsTotalCount, setSessionsTotalCount] = useState(0)
  // Committed user search (SearchField debounces). The API matches name, username or email,
  // or the exact id when a GUID is pasted.
  const [sessionsUserSearch, setSessionsUserSearch] = useState('')
  const [sessionsStatusFilter, setSessionsStatusFilter] = useState<SessionStatus | ''>('')

  // Session detail
  const [selectedSession, setSelectedSession] = useState<AdminSessionDetailDto | null>(null)
  const [sessionLocations, setSessionLocations] = useState<SessionLocationLogDto[]>([])
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false)
  const [sessionDetailError, setSessionDetailError] = useState<string | null>(null)

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [adventureToDelete, setAdventureToDelete] = useState<AdminAdventureDto | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteResult, setDeleteResult] = useState<AdminAdventureDeleteResult | null>(null)

  // Open validation items per adventure on the current page (Backlog 057), keyed by lowercased
  // id. From the User API's validation-counts, not the Admin API list; a missing id means zero.
  const [validationCounts, setValidationCounts] = useState<Record<string, number>>({})

  const fetchAdventures = useCallback(async () => {
    setLoading(true)
    setListError(null)
    try {
      const params: AdminAdventuresListParams = {
        page: page + 1, // API is 1-based
        pageSize,
        sortBy,
        sortDescending,
        search: search || undefined,
        status: statusFilter !== '' ? statusFilter : undefined,
        moderationStatus: moderationFilter !== '' ? moderationFilter : undefined,
      }
      const response = await adminApi.getAdventures(params)
      setAdventures(response.items)
      setTotalCount(response.totalCount)
    } catch (err) {
      setAdventures([])
      setListError(err instanceof Error ? err.message : 'Failed to fetch adventures')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sortBy, sortDescending, search, statusFilter, moderationFilter])

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getAdventureStats()
      setStats(response)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchAdventures()
  }, [fetchAdventures])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // One grouped call per page of rows. A failure (or an API without the endpoint yet) only
  // hides the badges; the list itself is unaffected.
  useEffect(() => {
    const ids = adventures.map((a) => a.id)
    if (ids.length === 0) {
      setValidationCounts({})
      return
    }
    let cancelled = false
    getAdventureValidationCounts(ids)
      .then((counts) => {
        if (cancelled) return
        const normalised: Record<string, number> = {}
        for (const [id, count] of Object.entries(counts)) normalised[id.toLowerCase()] = count
        setValidationCounts(normalised)
      })
      .catch((err) => {
        if (!cancelled) setValidationCounts({})
        console.warn('Failed to fetch validation counts:', err)
      })
    return () => {
      cancelled = true
    }
  }, [adventures])

  // Searching means best-match; not searching means newest first. Substring matching with a
  // sort that knows nothing about the term buries the row you are looking for: "Dix" is one of
  // 106 adventures whose title contains "dix" - Dixie National Forest supplies the rest - and
  // "Gro" is one of 3623, behind every Campground and Grotto. Both were pages deep.
  useEffect(() => {
    if (sortPinned) return
    setSortBy(search.trim() ? 'Relevance' : 'CreatedDate')
    setSortDescending(true)
  }, [search, sortPinned])

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDescending(!sortDescending)
    } else {
      setSortBy(field)
      setSortDescending(true)
    }
    // An explicit click outranks the automatic choice above, for the rest of the visit.
    setSortPinned(true)
    setPage(0)
  }

  // DataTable hands back the column's `sortKey`; every sortable column here declares one of the
  // SortField values the API takes.
  const handleSortChange = (sortKey: string) => handleSort(sortKey as SortField)

  const handleExport = async (adventure: AdminAdventureDto) => {
    setExporting(adventure.id)
    try {
      await adminApi.downloadAdventureExport(
        adventure.id,
        `${adventure.title.replace(/[^a-z0-9]/gi, '_')}-export.zip`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export adventure')
    } finally {
      setExporting(null)
    }
  }

  // There used to be View and Edit items pointing at /adventures/:id and /adventures/:id/edit,
  // neither of which exists in the portal. Editing stays on the player site (moderators have
  // full editor access there); the portal opens the player page and the creator edits from it.
  const handleViewAdventure = (adventure: AdminAdventureDto) => {
    window.open(`${PLAYER_SITE_URL}/adventures/${adventure.id}`, '_blank', 'noopener,noreferrer')
  }

  const handleDeleteClick = (adventure: AdminAdventureDto) => {
    setAdventureToDelete(adventure)
    setDeleteResult(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!adventureToDelete) return

    setDeleting(true)
    try {
      const result = await adminApi.deleteAdventure(adventureToDelete.id)
      setDeleteResult(result)
      // Refresh the list after successful deletion
      fetchAdventures()
      fetchStats()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete adventure')
      setDeleteDialogOpen(false)
      setAdventureToDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false)
    setAdventureToDelete(null)
    setDeleteResult(null)
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Sessions drawer handlers
  const fetchSessions = useCallback(async (adventureId: string) => {
    setSessionsLoading(true)
    setSessionsError(null)
    try {
      const params: AdminSessionsListParams = {
        adventureId,
        page: sessionsPage + 1,
        pageSize: sessionsPageSize,
        user: sessionsUserSearch || undefined,
        status: sessionsStatusFilter || undefined,
        sortBy: 'CreatedAt',
        sortDescending: true,
      }
      const response = await adminApi.getSessions(params)
      setSessions(response.items || [])
      setSessionsTotalCount(response.totalCount || 0)
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to fetch sessions')
    } finally {
      setSessionsLoading(false)
    }
  }, [sessionsPage, sessionsPageSize, sessionsUserSearch, sessionsStatusFilter])

  const handleOpenSessionsDrawer = (adventure: AdminAdventureDto) => {
    setSessionsAdventure(adventure)
    setSessionsDrawerOpen(true)
    setSessionsPage(0)
    setSessionsUserSearch('')
    setSessionsStatusFilter('')
    // No direct fetch: the effect below runs when `sessionsAdventure` changes. Fetching here too
    // sent every open twice.
  }

  const handleCloseSessionsDrawer = () => {
    setSessionsDrawerOpen(false)
    setSessionsAdventure(null)
    setSessions([])
    setSessionsError(null)
    setSelectedSession(null)
    setSessionLocations([])
    setSessionDetailError(null)
  }

  const handleSelectSession = async (session: AdminSessionDto) => {
    setSessionDetailLoading(true)
    setSessionDetailError(null)
    try {
      const [detail, locations] = await Promise.all([
        adminApi.getSession(session.id),
        adminApi.getSessionLocations(session.id),
      ])
      setSelectedSession(detail)
      setSessionLocations(locations)
    } catch (err) {
      setSessionDetailError(err instanceof Error ? err.message : 'Failed to fetch session details')
    } finally {
      setSessionDetailLoading(false)
    }
  }

  const handleBackToSessionsList = () => {
    setSelectedSession(null)
    setSessionLocations([])
    setSessionDetailError(null)
  }

  // Refetch sessions when filters change
  useEffect(() => {
    if (sessionsAdventure) {
      fetchSessions(sessionsAdventure.id)
    }
  }, [sessionsAdventure, fetchSessions])

  // A new user filter starts from the first page.
  useEffect(() => {
    setSessionsPage(0)
  }, [sessionsUserSearch])

  const formatSessionDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
  }

  const columns = useMemo<DataTableColumn<AdminAdventureDto>[]>(
    () => [
      {
        key: 'adventure',
        header: 'Adventure',
        sortKey: 'Title',
        // The card title on a phone.
        primary: true,
        render: (adventure) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={adventure.thumbnailUrl || undefined}
              variant="rounded"
              sx={{ width: 40, height: 40, bgcolor: adventure.thumbnailUrl ? undefined : 'primary.light' }}
            >
              <Explore />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight="bold">
                  {adventure.title}
                </Typography>
                <Tooltip title={adventure.isPublic ? 'Public' : 'Private'}>
                  {adventure.isPublic ? (
                    <Public fontSize="small" color="success" />
                  ) : (
                    <Lock fontSize="small" color="action" />
                  )}
                </Tooltip>
                {(validationCounts[adventure.id.toLowerCase()] ?? 0) > 0 && (
                  <Tooltip title="Open validation items. Open the row to see them.">
                    <Chip
                      icon={<FactCheck />}
                      label={`${validationCounts[adventure.id.toLowerCase()]} ${validationCounts[adventure.id.toLowerCase()] === 1 ? 'ticket' : 'tickets'}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Difficulty: {adventure.difficultyLevel}/5 | POIs: {adventure.poiCount}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        key: 'creator',
        header: 'Creator',
        sortKey: 'CreatorUsername',
        render: (adventure) => (
          <Typography variant="body2">{adventure.creatorDisplayName || 'Unknown'}</Typography>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortKey: 'Status',
        render: (adventure) => (
          <Chip
            label={ADVENTURE_STATUS_MAP[adventure.status] || adventure.status}
            color={getStatusColor(adventure.status)}
            size="small"
          />
        ),
      },
      {
        key: 'moderation',
        header: 'Moderation',
        render: (adventure) => (
          <Chip
            label={MODERATION_STATUS_MAP[adventure.moderationStatus] || adventure.moderationStatus}
            color={getModerationStatusColor(adventure.moderationStatus)}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        key: 'category',
        header: 'Category',
        hideBelow: 'lg',
        render: (adventure) => (
          <Typography variant="body2">
            {categories?.find((c) => c.id === adventure.categoryId)?.displayName || 'Unknown'}
          </Typography>
        ),
      },
      {
        key: 'participants',
        header: 'Participants',
        sortKey: 'ParticipantCount',
        hideBelow: 'md',
        render: (adventure) => (
          <>
            <Typography variant="body2">{adventure.participantCount}</Typography>
            <Typography variant="caption" color="text.secondary">
              {adventure.completionCount} completed
            </Typography>
          </>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        sortKey: 'CreatedDate',
        hideBelow: 'md',
        render: (adventure) => (
          <>
            <Typography variant="body2">{formatListDate(adventure.createdDate)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Updated: {formatListDate(adventure.updatedDate)}
            </Typography>
          </>
        ),
      },
    ],
    [categories, validationCounts]
  )

  const sessionColumns = useMemo<DataTableColumn<AdminSessionDto>[]>(
    () => [
      {
        key: 'user',
        header: 'User',
        primary: true,
        render: (session) => (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {session.userDisplayName}
            </Typography>
            {session.userEmail && (
              <Typography variant="caption" color="text.secondary" display="block">
                {session.userEmail}
              </Typography>
            )}
            {session.groupName && (
              <Typography variant="caption" color="text.secondary" display="block">
                Group: {session.groupName}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (session) => (
          <>
            <Chip
              icon={getSessionStatusIcon(session.status) || undefined}
              label={session.status}
              color={getSessionStatusColor(session.status)}
              size="small"
            />
            {session.isTestSession && (
              <Chip label="Test" size="small" variant="outlined" sx={{ ml: 0.5 }} />
            )}
          </>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        render: (session) => (
          <>
            <Typography variant="body2">{formatSessionDate(session.createdAt)}</Typography>
            {session.completedAt && (
              <Typography variant="caption" color="success.main" display="block">
                Completed: {formatSessionDate(session.completedAt)}
              </Typography>
            )}
            {session.abandonedAt && (
              <Typography variant="caption" color="warning.main" display="block">
                Abandoned: {formatSessionDate(session.abandonedAt)}
              </Typography>
            )}
          </>
        ),
      },
      {
        key: 'clues',
        header: 'Clues',
        align: 'right',
        render: (session) => <Typography variant="body2">{session.cluesUsed}</Typography>,
      },
    ],
    []
  )

  // Export is one labelled action (it used to be both an icon and a menu item); View opens the
  // player site; Delete is destructive so it lands in the overflow menu on a phone card.
  const getRowActions = (adventure: AdminAdventureDto): RowAction[] => [
    {
      key: 'details',
      label: 'Details',
      icon: <Info />,
      hint: 'Validation items, access rules and sessions',
      onClick: () => handleOpenSessionsDrawer(adventure),
    },
    {
      key: 'view',
      label: 'View',
      icon: <OpenInNew />,
      hint: 'Open on the player site (new tab)',
      onClick: () => handleViewAdventure(adventure),
    },
    {
      key: 'export',
      label: exporting === adventure.id ? 'Exporting...' : 'Export',
      icon: exporting === adventure.id ? <CircularProgress size={20} /> : <Download />,
      disabled: exporting === adventure.id,
      onClick: () => handleExport(adventure),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Delete />,
      color: 'error',
      onClick: () => handleDeleteClick(adventure),
    },
  ]

  type StatFilter = { key: 'status' | 'moderation'; value: number } | null
  const statCards: { label: string; count: number; color: string; filter: StatFilter }[] = stats
    ? [
        { label: 'Total', count: stats.totalCount, color: 'text.primary', filter: null },
        { label: 'Published', count: stats.publishedCount, color: 'success.main', filter: { key: 'status', value: 2 } },
        { label: 'Draft', count: stats.draftCount, color: 'text.secondary', filter: { key: 'status', value: 0 } },
        { label: 'Testing', count: stats.testingCount, color: 'info.main', filter: { key: 'status', value: 1 } },
        { label: 'Pending Review', count: stats.pendingModerationCount, color: 'warning.main', filter: { key: 'moderation', value: 0 } },
        { label: 'Archived', count: stats.archivedCount, color: 'warning.main', filter: { key: 'status', value: 3 } },
      ]
    : []

  // A card's number counts that one filter on its own, so applying it clears the other one.
  const applyStatCard = (filter: StatFilter, active: boolean) => {
    if (filter === null || active) {
      setFilters({ status: '', moderation: '' })
    } else if (filter.key === 'status') {
      setFilters({ status: filter.value, moderation: '' })
    } else {
      setFilters({ status: '', moderation: filter.value })
    }
  }

  const activeFilterCount = (statusFilter !== '' ? 1 : 0) + (moderationFilter !== '' ? 1 : 0)

  return (
    <PageShell
      title="Adventures"
      actions={
        activeTab === 'adventures' ? (
          <>
            <Button variant="outlined" startIcon={<Upload />} onClick={() => setImportDialogOpen(true)}>
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchAdventures()
                fetchStats()
              }}
            >
              Refresh
            </Button>
          </>
        ) : undefined
      }
    >
      <ResponsiveTabs tabs={TABS} value={activeTab} onChange={setActiveTab} sx={{ mb: 3 }} />

      {activeTab === 'adventures' && (
        <>
          {/* Stats Cards. Each one is also a filter: tapping "Pending Review" lists exactly the
              adventures it counts. Tapping the active one (or Total) clears it. */}
          {stats && (
            <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
              {statCards.map((card) => {
                const active =
                  card.filter === null
                    ? statusFilter === '' && moderationFilter === ''
                    : card.filter.key === 'status'
                      ? statusFilter === card.filter.value && moderationFilter === ''
                      : moderationFilter === card.filter.value && statusFilter === ''
                return (
                  <Grid item xs={6} sm={4} md={2} key={card.label}>
                    <Card
                      variant="outlined"
                      sx={{ borderColor: active ? 'primary.main' : undefined, borderWidth: active ? 2 : 1 }}
                    >
                      <CardActionArea
                        onClick={() => applyStatCard(card.filter, active)}
                        aria-pressed={active}
                        aria-label={`${card.label}: ${card.count}. ${active && card.filter ? 'Clear filter' : 'Filter the list'}`}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                          <Typography variant="h5" fontWeight="bold" color={card.color}>
                            {card.count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {card.label}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* The list is fetched one page at a time, so `total` is passed and DataTable shows the
              rows it was given rather than slicing them.

              A row opens the adventure's details drawer (validation items, why a user may not be
              able to start it, and its sessions). That drawer IS the portal's view of an
              adventure, so the row opens it, it is titled with the adventure, and "Details" is
              also an explicit labelled action for anyone who does not expect a row to open. */}
          <DataTable
            rows={adventures}
            columns={columns}
            getRowKey={(adventure) => adventure.id}
            loading={loading}
            error={listError}
            onRetry={fetchAdventures}
            emptyMessage={
              search || statusFilter !== '' || moderationFilter !== ''
                ? 'No adventures match these filters'
                : 'No adventures found'
            }
            page={page}
            pageSize={pageSize}
            total={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 25, 50, 100]}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSortChange={handleSortChange}
            onRowClick={handleOpenSessionsDrawer}
            getRowActions={getRowActions}
            toolbar={
              <>
                <SearchField
                  value={search}
                  onSearch={(q) => setFilters({ search: q })}
                  placeholder="Search by title or description"
                />
                <Button
                  variant={showFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </Button>
                {showFilters && (
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', width: '100%' }}>
                    <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => {
                          const val = e.target.value
                          setFilters({ status: val === '' ? '' : Number(val) })
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value={0}>Draft</MenuItem>
                        <MenuItem value={1}>Testing</MenuItem>
                        <MenuItem value={2}>Published</MenuItem>
                        <MenuItem value={3}>Archived</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
                      <InputLabel>Moderation</InputLabel>
                      <Select
                        value={moderationFilter}
                        label="Moderation"
                        onChange={(e) => {
                          const val = e.target.value
                          setFilters({ moderation: val === '' ? '' : Number(val) })
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value={0}>Pending</MenuItem>
                        <MenuItem value={1}>Approved</MenuItem>
                        <MenuItem value={2}>Rejected</MenuItem>
                        <MenuItem value={3}>Flagged</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant="text"
                      onClick={() => {
                        setFilters({ search: '', status: '', moderation: '' })
                        setSortPinned(false)
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                )}
              </>
            }
          />

          {/* Sessions Drawer */}
          <Drawer
            anchor="right"
            open={sessionsDrawerOpen}
            onClose={handleCloseSessionsDrawer}
            PaperProps={{ sx: { width: SESSIONS_DRAWER_WIDTH, maxWidth: '100vw' } }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              {selectedSession ? (
                /* Session Detail View */
                <>
                  {/* Header with Back Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton onClick={handleBackToSessionsList} aria-label="Back to sessions">
                        <ArrowBack />
                      </IconButton>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Session Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedSession.userDisplayName}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={handleCloseSessionsDrawer} aria-label="Close">
                      <Close />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {sessionDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : sessionDetailError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {sessionDetailError}
                    </Alert>
                  ) : (
                    <>
                      {/* Session Info Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Chip
                              icon={getSessionStatusIcon(selectedSession.status) || undefined}
                              label={selectedSession.status}
                              color={getSessionStatusColor(selectedSession.status)}
                              size="small"
                            />
                            {selectedSession.isTestSession && (
                              <Chip label="Test" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                            )}
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Lightbulb color="warning" fontSize="small" />
                              <Typography variant="h6">{selectedSession.cluesUsed}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">Clues Used</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* User Info */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          User Information
                        </Typography>
                        {/* One column on a phone, and long emails break instead of overflowing. */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">Display Name</Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {selectedSession.userDisplayName}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">Email</Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {selectedSession.userEmail || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">User ID</Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: 12 }}>
                              {selectedSession.userId}
                            </Typography>
                          </Box>
                          {selectedSession.groupName && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Group</Typography>
                              <Typography variant="body2">
                                <Group fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {selectedSession.groupName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>

                      {/* Timing Info */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          <Timer fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Timeline
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Created</Typography>
                            <Typography variant="body2">{formatSessionDate(selectedSession.createdAt)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Started</Typography>
                            <Typography variant="body2">{formatSessionDate(selectedSession.startedAt)}</Typography>
                          </Box>
                          {selectedSession.completedAt && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Completed</Typography>
                              <Typography variant="body2" color="success.main">
                                {formatSessionDate(selectedSession.completedAt)}
                              </Typography>
                            </Box>
                          )}
                          {selectedSession.abandonedAt && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Abandoned</Typography>
                              <Typography variant="body2" color="warning.main">
                                {formatSessionDate(selectedSession.abandonedAt)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>

                      {/* Stats */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Session Stats
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.nodeVisitCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Nodes Visited</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.locationLogCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Location Logs</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.cluesUsed}</Typography>
                            <Typography variant="caption" color="text.secondary">Clues Used</Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {/* Location Logs */}
                      {sessionLocations.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            Location History ({sessionLocations.length} points)
                          </Typography>
                          {/* Two lines per point instead of a four-column table: it fits a phone and
                              scrolls inside its own box. */}
                          <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {sessionLocations.map((loc, i) => (
                              <ListItem key={loc.id} divider={i < sessionLocations.length - 1} disableGutters>
                                <ListItemText
                                  primary={`${new Date(loc.timestamp).toLocaleTimeString()}  ·  ${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}`}
                                  secondary={[
                                    loc.accuracy ? `±${loc.accuracy.toFixed(0)} m` : 'accuracy N/A',
                                    loc.distanceFromPrevious ? `+${loc.distanceFromPrevious.toFixed(0)} m` : null,
                                  ]
                                    .filter(Boolean)
                                    .join('  ·  ')}
                                  primaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                          {sessionLocations.length > 0 && sessionLocations[sessionLocations.length - 1].totalDistanceTraveled && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Total Distance: {(sessionLocations[sessionLocations.length - 1].totalDistanceTraveled! / 1000).toFixed(2)} km
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </>
                  )}
                </>
              ) : (
                /* Sessions List View */
                <>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        Adventure details
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
                        {sessionsAdventure?.title}
                      </Typography>
                      {sessionsAdventure && (
                        <Button
                          size="small"
                          startIcon={<OpenInNew />}
                          onClick={() => handleViewAdventure(sessionsAdventure)}
                          sx={{ ml: -0.5 }}
                        >
                          View on player site
                        </Button>
                      )}
                    </Box>
                    <IconButton onClick={handleCloseSessionsDrawer} aria-label="Close">
                      <Close />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Validation items (read-only; each links to its ticket). Hidden when there
                      are none or the caller may not see them. */}
                  {sessionsAdventure && <AdventureValidationPanel adventureId={sessionsAdventure.id} />}

                  {/* Why a user may not be able to start it: its own gate and any Story rules (read-only). */}
                  {sessionsAdventure && <AdventureStoryAccessPanel adventureId={sessionsAdventure.id} />}

                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Sessions
                  </Typography>

                  {/* Filters */}
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <SearchField
                      value={sessionsUserSearch}
                      onSearch={setSessionsUserSearch}
                      placeholder="Search by user (name, email or ID)"
                    />
                    <FormControl size="small" sx={{ minWidth: 130, flex: { xs: '1 1 130px', sm: '0 0 auto' } }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={sessionsStatusFilter}
                        label="Status"
                        onChange={(e) => {
                          setSessionsStatusFilter(e.target.value as SessionStatus | '')
                          setSessionsPage(0)
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="InProgress">In Progress</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                        <MenuItem value="Abandoned">Abandoned</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Sessions Table. Server-paged like the list behind it, so `total` is passed
                      and DataTable renders exactly the page it was handed. */}
                  <DataTable
                    rows={sessions}
                    columns={sessionColumns}
                    getRowKey={(session) => session.id}
                    loading={sessionsLoading}
                    error={sessionsError}
                    onRetry={() => sessionsAdventure && fetchSessions(sessionsAdventure.id)}
                    emptyMessage={sessionsUserSearch ? 'No sessions match this user' : 'No sessions yet'}
                    dense
                    page={sessionsPage}
                    pageSize={sessionsPageSize}
                    total={sessionsTotalCount}
                    onPageChange={setSessionsPage}
                    onPageSizeChange={setSessionsPageSize}
                    pageSizeOptions={[10, 25, 50]}
                    onRowClick={handleSelectSession}
                  />
                </>
              )}
            </Box>
          </Drawer>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            maxWidth="sm"
            fullWidth
            {...mobileDialogProps}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {deleteResult ? (
                <>
                  <CheckCircle color="success" />
                  Adventure Deleted
                </>
              ) : (
                <>
                  <Warning color="error" />
                  Delete Adventure
                </>
              )}
            </DialogTitle>
            <DialogContent>
              {deleteResult ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {deleteResult.message}
                  </Alert>
                  <Typography variant="subtitle2" gutterBottom>
                    Deleted Items:
                  </Typography>
                  <List dense>
                    {deleteResult.deletedItems.sessionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.sessionsDeleted} sessions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.poisDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.poisDeleted} POIs`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.workflowVersionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.workflowVersionsDeleted} workflow versions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.requirementsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.requirementsDeleted} requirements`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.rewardsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.rewardsDeleted} rewards`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.tagsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.tagsDeleted} tags`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.ticketsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.ticketsDeleted} tickets`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.userProgressDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.userProgressDeleted} user progress records`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.discussionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.discussionsDeleted} discussions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.mediaFilesDeleted > 0 && (
                      <ListItem>
                        <ListItemText
                          primary={`${deleteResult.deletedItems.mediaFilesDeleted} media files`}
                          secondary={`${formatBytes(deleteResult.deletedItems.mediaFileSizeBytesFreed)} freed`}
                        />
                      </ListItem>
                    )}
                  </List>
                  {deleteResult.deletedItems.mediaFileDeletionErrors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Some media files could not be deleted:
                      </Typography>
                      {deleteResult.deletedItems.mediaFileDeletionErrors.map((error, i) => (
                        <Typography key={i} variant="body2">
                          {error}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This action cannot be undone. All data associated with this adventure will be permanently deleted.
                  </Alert>
                  {adventureToDelete && (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        Are you sure you want to delete <strong>{adventureToDelete.title}</strong>?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This will delete:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="All sessions and user progress" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All POIs and workflow versions" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All rewards, tickets, and tags" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All media files (images, etc.)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All discussions and comments" />
                        </ListItem>
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {deleteResult ? (
                <Button onClick={handleDeleteDialogClose} variant="contained">
                  Close
                </Button>
              ) : (
                <>
                  <Button onClick={handleDeleteDialogClose} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteConfirm}
                    color="error"
                    variant="contained"
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
                  >
                    {deleting ? 'Deleting...' : 'Delete Adventure'}
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          {/* Import Dialog */}
          <AdventureImportDialog
            open={importDialogOpen}
            onClose={() => setImportDialogOpen(false)}
            onComplete={() => {
              fetchAdventures()
              fetchStats()
            }}
          />
        </>
      )}

      {activeTab === 'categories' && <CategoryListTable />}
    </PageShell>
  )
}
